import './Stocks.scss';

import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import { STOCK_STATUS } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import useStocks from '@/common/hooks/stocks/useStocks';
import { ReactComponent as ChronoIcon } from '@/ebat/assets/icons/chrono.svg';
import getStockStatus from '@/ebat/components/blocks/Common/Stocks/getStockStatus';
// eslint-disable-next-line import/no-cycle
import StocksDetails from '@/ebat/components/blocks/Modal/Common/StocksDetails/StocksDetails';

import { StocksDefaultProps, StocksPropTypes } from './Stocks.proptypes';

const Stocks = ({
  categories,
  className,
  companyAvailability,
  delayQuantity,
  noDesktopLabel,
  disabled,
  isLoading,
  origin,
  status,
  stock,
  stocks,
  unit,
}) => {
  const { showStock, zergRush } = useAuth();
  const { t } = useTranslation();
  const {
    onOpenModal,
    setModalClassName,
    setModalContent,
    setModalSize,
  } = useModalContext();

  const { getStocks } = useStocks(stocks);

  const localStock = stocks?.filter((item) => item.quantity > 0)[0];

  const isDisabled = useMemo(
    () => ({
      disabled:
        isLoading ||
        disabled ||
        zergRush ||
        !showStock ||
        ![STOCK_STATUS.DELAI, STOCK_STATUS.DISPO].includes(status),
      loading: isLoading,
    }),
    [disabled, isLoading, showStock, status, zergRush],
  );

  const handleOpenModal = useCallback(() => {
    if (!isDisabled.disabled) {
      setModalContent(
        <StocksDetails
          categoryLabel={categories.length > 0 ? categories[0].label : ''}
          stocks={stocks}
          unit={unit}
        />,
      );
      setModalClassName('modal-stocks-details');
      setModalSize('sm');
      onOpenModal();
    }
  }, [
    categories,
    isDisabled,
    onOpenModal,
    setModalClassName,
    setModalContent,
    setModalSize,
    stocks,
    unit,
  ]);

  const statusValue = useMemo(() => {
    if (zergRush) return 'unknown';

    if (origin === 'checkout') {
      if (!companyAvailability) {
        return 'info';
      }
      if (stock === null) {
        return 'nodis';
      }
      if (stock.delay === 0) {
        return 'success';
      }
      if (stock.delay <= 24) {
        return 'delay';
      }
      return 'warning';
    }

    if (stock && !status) {
      if (stock.delay < 24) {
        return 'success';
      }

      if (stock.delay === 24) {
        return 'delay';
      }

      if (stock.delay > 24) {
        return 'warning';
      }
    }

    return getStockStatus(status, companyAvailability, localStock?.delay);
  }, [companyAvailability, localStock, origin, status, stock, zergRush]);

  if (origin === 'cart') {
    return (
      <div
        className={cn('stocks', className, {
          'stocks--no-desktop-label': noDesktopLabel,
        })}
        data-qa-id="stocks"
      >
        <ul className="stocks__list">
          {[STOCK_STATUS.DELAI, STOCK_STATUS.DISPO].includes(status) ? (
            <>
              {getStocks.available > 0 ? (
                <li className="stocks__item" data-qa-id="stocks-item">
                  <Button
                    className={cn(
                      'stocks__status',
                      'status',
                      'status--success',
                    )}
                    context="stock-cta"
                    disabled={isDisabled.disabled}
                    onClick={handleOpenModal}
                    value="success"
                  >
                    <span className="status__sticker" />
                    <span className="status__label">{getStocks.available}</span>
                  </Button>
                </li>
              ) : null}
              {getStocks.delayed > 0 ? (
                <li className="stocks__item" data-qa-id="stocks-item">
                  <Button
                    className={cn('stocks__status', 'status', 'status--delay')}
                    context="stock-cta"
                    disabled={isDisabled.disabled}
                    onClick={handleOpenModal}
                    value="delay"
                  >
                    <span className="status__delay">24h</span>
                    <ChronoIcon className="status__icon" />
                    <span className="status__label">{getStocks.delayed}</span>
                  </Button>
                </li>
              ) : null}
              {delayQuantity > 0 ? (
                <li className="stocks__item" data-qa-id="stocks-item">
                  <Button
                    className={cn(
                      'stocks__status',
                      'status',
                      'status--warning',
                    )}
                    context="stock-cta"
                    disabled={isDisabled.disabled}
                    onClick={handleOpenModal}
                    value="warning"
                  >
                    <span className="status__sticker" />
                    <span className="status__label">
                      {Math.round(delayQuantity)}
                    </span>
                  </Button>
                </li>
              ) : null}
            </>
          ) : (
            <li className="stocks__item" data-qa-id="stocks-item">
              {companyAvailability ? (
                <span
                  className={cn('stocks__status', 'status', 'status--nodis')}
                  data-qa-id="stock-cta-disabled"
                  data-qa-value="nodis"
                >
                  <ChronoIcon className="status__icon" />
                  <span className="status__label" />
                </span>
              ) : (
                <span
                  className={cn(
                    'stocks__status',
                    'status',
                    `status--${statusValue}`,
                  )}
                  data-qa-id="stock-cta-disabled"
                  data-qa-value={statusValue}
                >
                  <span className="status__sticker" />
                  <span className="status__label" />
                </span>
              )}
            </li>
          )}
        </ul>
      </div>
    );
  }

  const tranlationKeyPrefix =
    origin === 'checkout'
      ? 'ebat:entities:checkout.modal.stocks'
      : 'common:entities:common.modal.stocks';

  return (
    <div
      className={cn('stocks', className, {
        'stocks--no-desktop-label': noDesktopLabel,
      })}
      data-qa-id="stocks-item"
    >
      <Button
        className={cn('stocks__status', 'status', `status--${statusValue}`)}
        context="stock-cta"
        disabled={isDisabled.disabled}
        onClick={handleOpenModal}
        value={statusValue}
      >
        {['nodis', 'delay'].includes(statusValue) ? (
          <ChronoIcon className="status__icon" />
        ) : (
          <span className="status__sticker" />
        )}
        <span className="status__label">
          {origin === 'checkout' || origin === 'details' ? (
            <>
              {statusValue === 'nodis' && t(`${tranlationKeyPrefix}.nodis`)}
              {statusValue === 'info' && t(`${tranlationKeyPrefix}.info`)}
              {(statusValue === 'success' ||
                statusValue === 'delay' ||
                statusValue === 'warning') &&
                (stock?.delay > 72
                  ? t(`${tranlationKeyPrefix}.delay_in_days`, {
                      delay: stock?.delay / 24,
                    })
                  : t(`${tranlationKeyPrefix}.delay`, {
                      delay: stock?.delay,
                    }))}
            </>
          ) : (
            t(`ebat:messages:stocks.status.${statusValue}`, {
              delay: localStock?.delay || stock?.delay,
            })
          )}
        </span>
      </Button>
    </div>
  );
};

Stocks.defaultProps = {
  ...StocksDefaultProps,
};

Stocks.propTypes = {
  ...StocksPropTypes,
};

export default Stocks;
